// components/leads/LeadMoveDialogs.js
// Dialogs per transizioni lead che richiedono dati aggiuntivi

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Calendar, Clock, User, XCircle, CheckCircle, Loader2, AlertTriangle, Mail, Users } from 'lucide-react';
import { ParticipantsSection } from '../appointments/ParticipantsSection';

// Dialog per fissare appuntamento
export const AppointmentDialog = ({
  open,
  onOpenChange,
  lead,
  technicians = [],
  rooms = [],
  users = [], // Lista utenti CRM per partecipanti interni
  googleConnected = false, // Se Google Calendar è collegato
  onConfirm,
  isSubmitting = false
}) => {
  const [formData, setFormData] = useState({
    technician_id: lead?.assigned_technician_id || '',
    appointment_date: '',
    appointment_time: '',
    room_id: '',
    notes: '',
    send_confirmation_email: true
  });
  
  // Stato partecipanti
  const [internalParticipants, setInternalParticipants] = useState([]);
  const [externalParticipants, setExternalParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);

  // Reset form quando si apre con un nuovo lead
  useEffect(() => {
    if (open && lead) {
      setFormData({
        technician_id: lead.assigned_technician_id || '',
        appointment_date: '',
        appointment_time: '',
        room_id: '',
        notes: '',
        send_confirmation_email: !!lead.email // Default ON solo se il cliente ha email
      });
      // Reset partecipanti
      setInternalParticipants([]);
      setExternalParticipants([]);
      setShowParticipants(false);
    }
  }, [open, lead]);

  const isValid = formData.technician_id && formData.appointment_date && formData.appointment_time;
  const hasClientEmail = !!lead?.email;

  const handleSubmit = () => {
    if (isValid) {
      // Prepara payload con partecipanti
      const payload = {
        ...formData,
        participants_internal: internalParticipants,
        participants_external: externalParticipants
      };
      onConfirm(lead.id, 'appuntamento_fissato', payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#FF7819]" />
            Completa dati appuntamento
          </DialogTitle>
          <DialogDescription>
            Inserisci i dati per fissare l'appuntamento per {lead?.first_name} {lead?.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Tecnico */}
          <div>
            <Label className="text-sm font-medium flex items-center gap-1">
              <User className="w-4 h-4" />
              Tecnico assegnatario *
            </Label>
            <Select 
              value={formData.technician_id} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, technician_id: v }))}
            >
              <SelectTrigger className="mt-1" data-testid="appointment-technician-select">
                <SelectValue placeholder="Seleziona tecnico..." />
              </SelectTrigger>
              <SelectContent>
                {technicians.map(tech => (
                  <SelectItem key={tech.id} value={tech.id}>
                    {tech.first_name} {tech.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Data e Ora */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Data appuntamento *
              </Label>
              <Input
                type="date"
                value={formData.appointment_date}
                onChange={(e) => setFormData(prev => ({ ...prev, appointment_date: e.target.value }))}
                className="mt-1"
                min={new Date().toISOString().split('T')[0]}
                data-testid="appointment-date-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Ora appuntamento *
              </Label>
              <Input
                type="time"
                value={formData.appointment_time}
                onChange={(e) => setFormData(prev => ({ ...prev, appointment_time: e.target.value }))}
                className="mt-1"
                data-testid="appointment-time-input"
              />
            </div>
          </div>

          {/* Sala/Luogo (opzionale) */}
          {rooms.length > 0 && (
            <div>
              <Label className="text-sm font-medium">Sala/Luogo (opzionale)</Label>
              <Select 
                value={formData.room_id} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, room_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona sala..." />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map(room => (
                    <SelectItem key={room.id} value={room.id}>
                      {room.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Note */}
          <div>
            <Label className="text-sm font-medium">Note (opzionale)</Label>
            <Textarea
              placeholder="Aggiungi note sull'appuntamento..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="mt-1"
              data-testid="appointment-notes-textarea"
            />
          </div>

          {/* Checkbox Email Conferma */}
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox
              id="send_confirmation_email"
              checked={formData.send_confirmation_email}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, send_confirmation_email: checked }))}
              disabled={!hasClientEmail}
              data-testid="send-confirmation-email-checkbox"
            />
            <div className="grid gap-1.5 leading-none">
              <Label 
                htmlFor="send_confirmation_email" 
                className={`text-sm font-medium flex items-center gap-1.5 cursor-pointer ${!hasClientEmail ? 'text-gray-400' : ''}`}
              >
                <Mail className="w-4 h-4" />
                Invia email di conferma al cliente
              </Label>
              {!hasClientEmail && (
                <p className="text-xs text-amber-600">
                  Il cliente non ha un indirizzo email
                </p>
              )}
            </div>
          </div>

          {/* Sezione Partecipanti (espandibile) */}
          <div className="pt-3 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowParticipants(!showParticipants)}
              className="w-full justify-start text-sm text-gray-600 hover:text-gray-900"
              data-testid="toggle-participants-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              {showParticipants ? 'Nascondi' : 'Gestisci'} partecipanti
              {(internalParticipants.length > 0 || externalParticipants.length > 0) && (
                <span className="ml-2 bg-[#FF7819]/10 text-[#FF7819] px-2 py-0.5 rounded text-xs">
                  {internalParticipants.length + externalParticipants.length}
                </span>
              )}
            </Button>
            
            {showParticipants && (
              <div className="mt-3">
                <ParticipantsSection
                  lead={lead}
                  users={users}
                  internalParticipants={internalParticipants}
                  externalParticipants={externalParticipants}
                  onInternalChange={setInternalParticipants}
                  onExternalChange={setExternalParticipants}
                  googleConnected={googleConnected}
                  sendClientEmail={formData.send_confirmation_email}
                  onSendClientEmailChange={(checked) => setFormData(prev => ({ ...prev, send_confirmation_email: checked }))}
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="bg-[#FF7819] hover:bg-[#E56A10]"
            data-testid="confirm-appointment-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Fissa Appuntamento
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog per segnare come perso
export const LostReasonDialog = ({
  open,
  onOpenChange,
  lead,
  onConfirm,
  isSubmitting = false
}) => {
  const [formData, setFormData] = useState({
    reason: '',
    notes: ''
  });

  const lostReasons = [
    { value: 'prezzo_alto', label: 'Prezzo troppo alto' },
    { value: 'tempistiche', label: 'Tempistiche non compatibili' },
    { value: 'concorrenza', label: 'Scelta della concorrenza' },
    { value: 'non_interessato', label: 'Non più interessato' },
    { value: 'non_fattibile', label: 'Lavoro non fattibile' },
    { value: 'no_risposta', label: 'Nessuna risposta' },
    { value: 'altro', label: 'Altro' },
  ];

  // Reset form quando si apre
  useEffect(() => {
    if (open) {
      setFormData({ reason: '', notes: '' });
    }
  }, [open]);

  const isValid = formData.reason;

  const handleSubmit = () => {
    if (isValid) {
      onConfirm(lead.id, 'perso', formData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="w-5 h-5" />
            Motivo perdita lead
          </DialogTitle>
          <DialogDescription>
            Indica il motivo per cui il lead {lead?.first_name} {lead?.last_name} è stato perso
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Motivo */}
          <div>
            <Label className="text-sm font-medium">Motivo perdita *</Label>
            <Select 
              value={formData.reason} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, reason: v }))}
            >
              <SelectTrigger className="mt-1" data-testid="lost-reason-select">
                <SelectValue placeholder="Seleziona motivo..." />
              </SelectTrigger>
              <SelectContent>
                {lostReasons.map(reason => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Note */}
          <div>
            <Label className="text-sm font-medium">Note aggiuntive (opzionale)</Label>
            <Textarea
              placeholder="Aggiungi dettagli..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="mt-1"
              data-testid="lost-notes-textarea"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="bg-red-500 hover:bg-red-600"
            data-testid="confirm-lost-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 mr-2" />
                Segna come Perso
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog per conferma conversione
export const ConvertConfirmDialog = ({
  open,
  onOpenChange,
  lead,
  hasEstimate = false,
  hasContract = false,
  onConfirm,
  isSubmitting = false
}) => {
  const canConvert = hasEstimate || hasContract;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            Converti Lead
          </DialogTitle>
          <DialogDescription>
            Conferma la conversione del lead {lead?.first_name} {lead?.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {canConvert ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Questo lead verrà segnato come convertito.
              </p>
              {hasEstimate && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Preventivo presente
                </p>
              )}
              {hasContract && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Contratto presente
                </p>
              )}
            </div>
          ) : (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>
                  Per convertire questo lead è necessario avere almeno un preventivo o un contratto collegato.
                  Crea prima un preventivo dalla scheda del lead.
                </span>
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {canConvert ? 'Annulla' : 'Chiudi'}
          </Button>
          {canConvert && (
            <Button
              onClick={() => onConfirm(lead.id, 'convertito', {})}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-convert-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Conversione...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Converti Lead
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
